// src/project/SampleStorage.js
// IndexedDB wrapper for storing imported audio sample blobs.
// Database: "helix_samples", Object store: "samples"
// Each entry: { ref, name, blob: ArrayBuffer, size: number, created: ISO string }

export class SampleStorage {
  constructor(dbName = 'helix_samples') {
    this.dbName = dbName;
    this._db = null;
  }

  /** Open (or create) the IndexedDB database. Idempotent. */
  async open() {
    if (this._db) return;
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(this.dbName, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('samples')) {
          const store = db.createObjectStore('samples', { keyPath: 'ref' });
          store.createIndex('name', 'name', { unique: false });
          store.createIndex('created', 'created', { unique: false });
        }
      };
      req.onsuccess = () => {
        this._db = req.result;
        resolve();
      };
      req.onerror = () => reject(req.error);
    });
  }

  /** Generate a unique ref string for a sample. */
  generateRef() {
    return `samp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }

  /**
   * Store an audio ArrayBuffer in IndexedDB.
   * @param {string} ref - unique reference key
   * @param {ArrayBuffer} blob - raw audio data
   * @param {string} name - original filename for display
   */
  async storeSample(ref, blob, name) {
    await this.open();
    return new Promise((resolve, reject) => {
      const tx = this._db.transaction('samples', 'readwrite');
      tx.objectStore('samples').put({ ref, name, blob, size: blob.byteLength, created: new Date().toISOString() });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Retrieve an audio ArrayBuffer by ref. Returns null if not found. */
  async getSample(ref) {
    await this.open();
    return new Promise((resolve, reject) => {
      const tx = this._db.transaction('samples', 'readonly');
      const req = tx.objectStore('samples').get(ref);
      req.onsuccess = () => resolve(req.result ? req.result.blob : null);
      req.onerror = () => reject(req.error);
    });
  }

  /** Delete a sample by ref. */
  async deleteSample(ref) {
    await this.open();
    return new Promise((resolve, reject) => {
      const tx = this._db.transaction('samples', 'readwrite');
      tx.objectStore('samples').delete(ref);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Get metadata for all stored samples (without loading blobs). */
  async getAllMeta() {
    await this.open();
    return new Promise((resolve, reject) => {
      const tx = this._db.transaction('samples', 'readonly');
      const req = tx.objectStore('samples').getAll();
      req.onsuccess = () => {
        const all = req.result || [];
        resolve(all.map(({ ref, name, size, created }) => ({ ref, name, size, created })));
      };
      req.onerror = () => reject(req.error);
    });
  }

  /** Close the database connection. */
  close() {
    if (this._db) { this._db.close(); this._db = null; }
  }
}
